<template>
  <div class="q-pa-md" @dragenter="handleDragEnter" @dragover.prevent="handleDragOver" @dragleave="handleDragLeave">
    <!-- Drag overlay with visual feedback -->
    <div
      v-if="isDragOver || isProcessingDrop"
      class="drag-overlay"
      :class="{ processing: isProcessingDrop }"
      @dragover.prevent="handleDragOver"
      @drop.prevent="handleDrop"
      @dragleave="handleDragLeave"
    >
      <div class="drag-content">
        <template v-if="isProcessingDrop">
          <q-spinner-pie color="primary" size="48px" class="q-mb-md" />
          <div class="drag-text">Processing files...</div>
          <div class="drag-subtext">Preparing upload dialog</div>
        </template>
        <template v-else>
          <q-icon name="cloud_upload" size="64px" color="primary" class="q-mb-md" />
          <div class="drag-text">Drop files here to upload</div>
          <div class="drag-subtext">Supported: JPEG, JPG, PNG, MP4, MOV, PDF, DOC, DOCX</div>
        </template>
      </div>
    </div>

    <template v-if="enhancedContent.length > 0">
      <transition name="fade-scale">
        <div class="dynamic-island" v-if="selectedContent.length > 0">
          <div class="dynamic-island-content">
            <!-- Center section with total count (moved to left) -->
            <div class="left-section">
              <span class="total-count">{{ enhancedContent.length }} / {{ pagination.total }} files</span>
            </div>

            <!-- Vertical divider -->
            <div class="vertical-divider"></div>

            <!-- Left section with selection count and type chips (moved to center) -->
            <div class="center-section">
              <span class="selection-count-text">{{ selectedContent.length }} selected</span>

              <div class="selection-type-chips">
                <!-- Image type chip with count -->
                <q-chip v-if="selectedImagesCount > 0" icon="o_image" class="content-type-chip image-chip">
                  {{ selectedImagesCount }}
                  <q-tooltip ref="selectedImagesChipTooltip">Selected images</q-tooltip>
                </q-chip>

                <!-- Video type chip with count -->
                <q-chip v-if="selectedVideosCount > 0" icon="o_movie" class="content-type-chip video-chip">
                  {{ selectedVideosCount }}
                  <q-tooltip ref="selectedVideosChipTooltip">Selected videos</q-tooltip>
                </q-chip>

                <!-- Document type chip with count -->
                <q-chip v-if="selectedDocumentsCount > 0" icon="o_description" class="content-type-chip document-chip">
                  {{ selectedDocumentsCount }}
                  <q-tooltip ref="selectedDocumentsChipTooltip">Selected documents</q-tooltip>
                </q-chip>
              </div>
            </div>

            <!-- Vertical divider -->
            <div class="vertical-divider"></div>

            <!-- Right action buttons section -->
            <div class="right-section">
              <q-btn
                class="action-button"
                icon="o_file_download"
                flat
                dense
                :disabled="selectedContent.length === 0"
                @click="
                  hideAllTooltips();
                  handleBatchDownloadExisting();
                "
              >
                <q-tooltip ref="downloadTooltip">Download Files</q-tooltip>
              </q-btn>

              <q-btn
                class="action-button"
                icon="o_open_in_new"
                flat
                dense
                :disabled="selectedContent.length === 0"
                @click="
                  hideAllTooltips();
                  handleExport();
                "
                :loading="isExporting"
                :percentage="exportProgress"
              >
                <q-tooltip ref="exportTooltip">Smart Export</q-tooltip>
              </q-btn>

              <q-btn
                class="action-button"
                icon="o_link"
                flat
                dense
                :disabled="selectedContent.length === 0"
                @click="
                  hideAllTooltips();
                  handleShareSelectedContent();
                "
              >
                <q-tooltip ref="shareTooltip">Share Link</q-tooltip>
              </q-btn>

              <q-btn
                v-if="selectedCollectionId"
                class="action-button"
                icon="o_delete_outline"
                flat
                dense
                :disabled="selectedContent.length === 0"
                @click="
                  hideAllTooltips();
                  handleRemoveFromCollection();
                "
                :loading="isRemovingFromCollection"
              >
                <q-tooltip ref="removeCollectionTooltip">Remove from Collection</q-tooltip>
              </q-btn>

              <q-btn
                v-if="!selectedCollectionId"
                class="action-button"
                icon="o_create_new_folder"
                flat
                dense
                :disabled="selectedContent.length === 0"
                @click="
                  hideAllTooltips();
                  openAddToCollectionDialog();
                "
                :loading="isAddingToCollection"
              >
                <q-tooltip ref="addCollectionTooltip">Add to Collection</q-tooltip>
              </q-btn>

              <q-btn id="more-actions" class="action-button more-actions-btn" icon="o_more_horiz" flat dense :disabled="selectedContent.length === 0" @click="hideAllTooltips()">
                <q-tooltip ref="moreActionsTooltip">More Actions</q-tooltip>
                <q-menu
                  anchor="bottom right"
                  self="top right"
                  class="gallery-menu"
                  style="max-height: none !important; overflow-y: visible !important"
                  @show="isMoreActionsMenuOpen = true"
                  @hide="isMoreActionsMenuOpen = false"
                >
                  <q-list style="width: auto" class="gallery-menu-list">
                    <q-item clickable v-close-popup @click="openAddLabelDialog(null)" class="gallery-menu-item">
                      <q-item-section avatar>
                        <q-icon name="o_local_offer" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label class="gallery-menu-label">Add Tag</q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-item clickable v-close-popup @click="handleOpenDeleteConfirmation(null)" class="gallery-menu-item">
                      <q-item-section avatar>
                        <q-icon name="o_delete" color="negative" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label class="gallery-menu-label warning-label">Delete</q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
              </q-btn>

              <!-- Vertical divider before close button -->
              <div class="vertical-divider"></div>

              <q-btn
                class="action-button close-btn"
                icon="o_close"
                flat
                dense
                @click="
                  hideAllTooltips();
                  deselectAll();
                "
              >
                <q-tooltip ref="closeTooltip">Close</q-tooltip>
              </q-btn>
            </div>
          </div>
        </div>
      </transition>

      <!-- Select All FAB that disappears when all images are selected -->
      <transition name="fade-scale">
        <q-btn
          v-if="selectedContent.length < enhancedContent.length && enhancedContent.length > 0"
          fab
          icon="o_library_add_check"
          color="grey-3"
          text-color="secondary"
          class="select-all-fab"
          @click="
            hideAllTooltips();
            toggleSelectAll();
          "
        >
          <q-tooltip ref="selectAllTooltip">Select All Files</q-tooltip>
        </q-btn>
      </transition>

      <div class="images-grid">
        <div
          v-for="content in enhancedContent"
          :key="content.id"
          class="grid-item"
          :class="[
            getImageClass(content),
            {
              selected: isSelected(content),
              'active-dropdown': activeDropdownImageId === content.id,
              'processing-item':
                (getContentType(content) === 'video' && videoProcessingStatuses[content.id] && videoProcessingStatuses[content.id] !== 'finished') ||
                (getContentType(content) === 'document' && isDocumentProcessing(content))
            }
          ]"
          @click="isVideoProcessing(content) || isDocumentProcessing(content) ? null : handleContentClick(content)"
        >
          <div class="selection-overlay" v-if="isSelected(content)">
            <q-icon name="check_circle" size="32px" color="white" />
          </div>

          <!-- Display videos differently than images -->
          <template v-if="getContentType(content) === 'video'">
            <div class="video-container">
              <!-- Only show video content when NOT processing -->
              <template v-if="!isVideoProcessing(content)">
                <q-img v-if="content.preview_url" :src="content.preview_url" :placeholder-src="content.placeholder_url" class="grid-image" fit="cover" />
                <div v-else class="grid-image video-placeholder">
                  <q-icon name="play_circle_filled" size="48px" color="white" />
                </div>
                <div class="video-duration" v-if="content.duration">
                  {{ formatDuration(content.duration) }}
                </div>
              </template>

              <!-- Processing overlay with minimal background -->
              <div
                v-if="isVideoProcessing(content)"
                class="video-processing-overlay"
                :class="{
                  'status-pending': videoProcessingStatuses[content.id] === 'pending',
                  'status-processing': videoProcessingStatuses[content.id] === 'processing',
                  'status-failed': videoProcessingStatuses[content.id] === 'failed'
                }"
              >
                <!-- Simple placeholder background when processing -->
                <div class="processing-placeholder"></div>
                <div class="processing-indicator">
                  <q-circular-progress v-if="videoProcessingStatuses[content.id] === 'processing'" indeterminate size="42px" color="primary" track-color="grey-6" class="q-mb-sm" />
                  <q-circular-progress v-else-if="videoProcessingStatuses[content.id] === 'pending'" show-value size="42px" color="primary" track-color="grey-6" class="q-mb-sm">
                    <q-icon name="hourglass_empty" size="24px" color="grey-6" />
                  </q-circular-progress>
                  <q-circular-progress v-else-if="videoProcessingStatuses[content.id] === 'failed'" size="42px" color="negative" value="100" track-color="grey-6" class="q-mb-sm">
                    <q-icon name="error_outline" size="24px" color="grey-6" />
                  </q-circular-progress>

                  <div class="processing-status-text">{{ getProcessingStatusText(content) }}</div>
                </div>
              </div>
            </div>
          </template>

          <!-- Document content -->
          <template v-else-if="getContentType(content) === 'document'">
            <div class="document-container">
              <!-- Only show document content when NOT processing -->
              <template v-if="!isDocumentProcessing(content)">
                <DocumentThumbnail :content="content" :documentProcessingStatuses="documentProcessingStatuses" class="grid-image" />
              </template>

              <!-- Processing overlay with minimal background -->
              <div
                v-if="isDocumentProcessing(content)"
                class="document-processing-overlay"
                :class="{
                  'status-pending': documentProcessingStatuses[content.id] && documentProcessingStatuses[content.id].status === 'pending',
                  'status-processing': documentProcessingStatuses[content.id] && documentProcessingStatuses[content.id].status === 'processing',
                  'status-failed': documentProcessingStatuses[content.id] && documentProcessingStatuses[content.id].status === 'failed'
                }"
              >
                <!-- Simple placeholder background when processing -->
                <div class="processing-placeholder"></div>
                <div class="processing-indicator">
                  <q-circular-progress
                    v-if="documentProcessingStatuses[content.id] && documentProcessingStatuses[content.id].status === 'processing'"
                    indeterminate
                    size="42px"
                    color="primary"
                    track-color="grey-6"
                    class="q-mb-sm"
                  />
                  <q-circular-progress
                    v-else-if="documentProcessingStatuses[content.id] && documentProcessingStatuses[content.id].status === 'pending'"
                    show-value
                    size="42px"
                    color="primary"
                    track-color="grey-6"
                    class="q-mb-sm"
                  >
                    <q-icon name="hourglass_empty" size="24px" color="grey-6" />
                  </q-circular-progress>
                  <q-circular-progress
                    v-else-if="documentProcessingStatuses[content.id] && documentProcessingStatuses[content.id].status === 'failed'"
                    size="42px"
                    color="negative"
                    value="100"
                    track-color="grey-6"
                    class="q-mb-sm"
                  >
                    <q-icon name="error_outline" size="24px" color="grey-6" />
                  </q-circular-progress>

                  <div class="processing-status-text">{{ getProcessingStatusText(content) }}</div>
                </div>
              </div>
            </div>
          </template>

          <!-- Image content (fallback) -->
          <q-img v-else :src="content.preview_url" :placeholder-src="content.placeholder_url" class="grid-image" fit="cover" />

          <!-- Only show regular image overlay for finished content (videos, documents, or images) -->
          <div v-if="!isAnyContentProcessing(content)" class="image-overlay">
            <div class="overlay-info">
              <q-chip icon="o_camera" text-color="grey-6" square class="overlay-chip content-id-chip">
                {{ content.type === 'video' ? content.id : content.shoot_id }}
              </q-chip>
              <q-chip icon="o_description" text-color="grey-6" square class="overlay-chip content-type-chip">
                {{ getFileExtension(content.filename || content.name) }}
              </q-chip>
              <q-chip icon="o_image" text-color="grey-6" v-if="content.width && content.height" square class="overlay-chip content-dimensions-chip">
                {{ `${content.width}x${content.height}` }}
              </q-chip>
            </div>
            <div class="action-buttons">
              <q-btn color="black" flat round class="action-btn menu-btn" @click.stop>
                <q-icon name="more_horiz" color="secondary" size="18px" />
                <q-menu
                  anchor="bottom right"
                  self="top right"
                  transition-show="jump-down"
                  transition-hide="jump-up"
                  :offset="[0, 10]"
                  class="gallery-menu"
                  @show="activeDropdownImageId = content.id"
                  @hide="activeDropdownImageId = null"
                >
                  <q-list style="width: auto; height: auto" class="gallery-menu-list">
                    <!-- Show only delete option when any content is processing -->
                    <template v-if="isAnyContentProcessing(content)">
                      <q-item clickable v-close-popup @click="handleOpenDeleteConfirmation(content)" class="gallery-menu-item">
                        <q-item-section avatar>
                          <q-icon name="o_delete" color="negative" />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label class="gallery-menu-label warning-label">Delete</q-item-label>
                        </q-item-section>
                      </q-item>
                    </template>

                    <!-- Show all options when no content is processing -->
                    <template v-else>
                      <q-item clickable v-close-popup @click="handleLightboxOrVideo(content)" class="gallery-menu-item">
                        <q-item-section avatar>
                          <q-icon name="o_fullscreen" color="black" />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label class="gallery-menu-label custom-label">Open in Lightbox</q-item-label>
                        </q-item-section>
                      </q-item>

                      <q-item clickable v-close-popup @click="handleDownload(content)" :disable="downloadingStates[content.id]" class="gallery-menu-item">
                        <q-item-section avatar>
                          <q-icon name="o_download" :color="downloadingStates[content.id] ? 'grey-6' : 'black'" />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label class="gallery-menu-label custom-label">Download</q-item-label>
                        </q-item-section>
                      </q-item>

                      <q-item clickable v-close-popup @click="handleCopyLink(content)" class="gallery-menu-item">
                        <q-item-section avatar>
                          <q-icon name="o_link" color="black" />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label class="gallery-menu-label custom-label">Share Link</q-item-label>
                        </q-item-section>
                      </q-item>

                      <q-item v-if="getContentType(content) === 'image'" clickable v-close-popup @click="openEditor(content)" class="gallery-menu-item">
                        <q-item-section avatar>
                          <q-icon name="o_edit" color="black" />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label class="gallery-menu-label custom-label">Edit Image</q-item-label>
                        </q-item-section>
                      </q-item>

                      <q-item v-if="getContentType(content) === 'image'" clickable v-close-popup @click="openVideoGenerator(content)" class="gallery-menu-item">
                        <q-item-section avatar>
                          <q-icon name="o_movie" color="black" />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label class="gallery-menu-label custom-label">Generate Video</q-item-label>
                        </q-item-section>
                      </q-item>

                      <q-item v-if="(role === 'admin' || role === 'client') && getContentType(content) === 'image'" clickable v-close-popup @click="shareToMeta(content)" class="gallery-menu-item">
                        <q-item-section avatar>
                          <q-icon name="o_share" color="black" />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label class="gallery-menu-label custom-label">Share on Meta</q-item-label>
                        </q-item-section>
                      </q-item>

                      <q-item clickable v-close-popup @click="openAddLabelDialog(content)" class="gallery-menu-item">
                        <q-item-section avatar>
                          <q-icon name="o_local_offer" />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label class="gallery-menu-label custom-label">Add Tag</q-item-label>
                        </q-item-section>
                      </q-item>

                      <q-item clickable v-close-popup @click="handleOpenDeleteConfirmation(content)" class="gallery-menu-item">
                        <q-item-section avatar>
                          <q-icon name="o_delete" color="negative" />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label class="gallery-menu-label warning-label">Delete</q-item-label>
                        </q-item-section>
                      </q-item>
                    </template>
                  </q-list>
                </q-menu>
              </q-btn>
            </div>

            <!-- Image name display and edit section at the bottom -->
            <div class="image-name-container" @click.stop>
              <div class="image-name-display" v-if="editingImageId !== content.id">
                <span class="image-name-text">{{ content.name || content.filename }}</span>
                <q-btn flat round dense size="sm" class="edit-name-btn" @click.stop="startEditingName(content)">
                  <q-icon name="edit" size="16px" color="white" />
                  <q-tooltip>Edit {{ getContentTypeLabel(content) }} Name</q-tooltip>
                </q-btn>
              </div>
              <div class="image-name-edit-wrapper" v-else>
                <input
                  ref="nameEditField"
                  type="text"
                  class="image-name-edit"
                  @keydown.enter.prevent="saveImageName(content)"
                  @keydown.esc.prevent="cancelEditing"
                  @blur="handleInputBlur(content)"
                  v-model="editingNameValue"
                  @click.stop
                  spellcheck="false"
                />
                <q-btn flat round dense size="sm" class="cancel-edit-btn" @click.stop="cancelEditing">
                  <q-icon name="close" size="16px" color="white" />
                </q-btn>
              </div>
            </div>
          </div>
          <!-- Action buttons for any processing content (videos or documents) - always visible -->
          <div v-if="isAnyContentProcessing(content)" class="action-buttons processing-actions">
            <q-btn color="black" flat round class="action-btn menu-btn" @click.stop>
              <q-icon name="more_horiz" color="secondary" size="18px" />
              <q-menu
                anchor="bottom right"
                self="top right"
                transition-show="jump-down"
                transition-hide="jump-up"
                :offset="[0, 10]"
                class="gallery-menu"
                @show="activeDropdownImageId = content.id"
                @hide="activeDropdownImageId = null"
              >
                <q-list style="width: auto; height: auto" class="gallery-menu-list">
                  <q-item clickable v-close-popup @click="handleOpenDeleteConfirmation(content)" class="gallery-menu-item">
                    <q-item-section avatar>
                      <q-icon name="o_delete" color="negative" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label class="gallery-menu-label warning-label">Delete</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </div>
      </div>
    </template>

    <!-- Empty State with drag and drop functionality -->
    <div v-else class="empty-state-container" @dragenter="handleDragEnter" @dragover.prevent="handleDragOver" @dragleave="handleDragLeave" @drop.prevent="handleDrop">
      <div class="empty-state-content">
        <q-icon name="o_photo_library" size="60px" color="grey-5" class="q-mb-md" />
        <div class="empty-state-title">No Content Found</div>
        <div class="empty-state-subtitle">Drag and drop files here to get started</div>
        <div class="empty-state-hint">
          <q-icon name="info" size="16px" color="grey-6" class="q-mr-sm" />
          <span>Supported formats: JPEG, JPG, MP4, MOV</span>
        </div>
      </div>
    </div>

    <!-- Video Generator Component -->
    <VideoGenerator v-if="selectedImageForVideo" :image="selectedImageForVideo" :isOpen.sync="showVideoGenerator" @update:isOpen="handleVideoGeneratorClose" />

    <!-- Removal Confirmation Dialog -->
    <q-dialog v-model="showRemoveConfirmDialog" transition-show="scale" transition-hide="scale" seamless>
      <q-card class="modal-card">
        <div class="modal-pattern-bg delete-pattern"></div>
        <q-card-section class="modal-header delete-header">
          <q-icon name="remove_circle" color="negative" class="modal-header-icon" />
          <div class="modal-title delete-title">Remove from Collection</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup class="close-btn" />
        </q-card-section>

        <q-separator class="delete-separator" />

        <q-card-section class="modal-body">
          <div class="warning-box">
            <q-icon name="warning" color="negative" class="warning-icon" />
            <div class="warning-text">This will remove the selected content from this collection.</div>
          </div>

          <p class="confirmation-text">Do you want to remove {{ selectedContent.length }} content{{ selectedContent.length !== 1 ? 's' : '' }} from this collection?</p>

          <div class="note-text">
            <q-icon name="info" class="note-icon" />
            <div>This will only remove the content from the collection, not delete them from your library.</div>
          </div>
        </q-card-section>

        <q-card-actions class="modal-actions">
          <q-btn flat no-caps label="Cancel" color="grey-7" v-close-popup class="modal-btn cancel-btn btn-with-transition" />
          <q-btn flat no-caps label="Remove" :loading="isRemovingFromCollection" @click="confirmRemoveFromCollection" class="modal-btn delete-btn btn-with-transition" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <ImageFormatDialog v-if="showExportDialog" v-model="showExportDialog" :selectedContent="selectedContent" @format="exportImages" />
    <q-dialog v-if="showShareDialog" v-model="showShareDialog" @hide="selectedShareImage = null" maximized>
      <MetaShareDialog
        :metaIntegration="metaIntegration"
        :mediaUrl="selectedShareImage.url"
        :previewUrl="selectedShareImage.preview_url"
        :placeholderUrl="selectedShareImage.placeholder_url"
        :imageRatio="selectedShareImage.aspect_ratio"
        @hide="selectedShareImage = null"
      />
    </q-dialog>
    <GalleryLightbox
      v-if="lightboxOpen"
      ref="galleryLightbox"
      :content="enhancedContent"
      :documentProcessingStatuses="documentProcessingStatuses"
      :startIndex="lightboxStartIndex"
      @close="lightboxOpen = false"
      @download="handleLightboxDownload"
      @edit="openEditor"
      @generateVideo="openVideoGenerator"
      @shareToMeta="shareToMeta"
      @version-added="handleVersionAdded"
      @version-changed="handleVersionChanged"
      @content-processing-completed="handleContentProcessingCompleted"
      @content-updated="$emit('content-updated', $event)"
      @image-updated="handleImageUpdated"
    />
    <AddToCollectionDialog
      :show="showAddToCollectionDialog"
      @update:show="showAddToCollectionDialog = $event"
      :selectedContent="selectedContent"
      @add-success="handleAddToCollectionSuccess"
      @loading-state="isAddingToCollection = $event"
    />

    <!-- Delete Confirmation Dialog -->
    <DeleteConfirmationDialog />
    <!-- Add Label Dialog -->
    <AddLabelDialog />

    <!-- Share Content Dialog -->
    <ShareContentDialog
      :show="showShareContentDialog"
      @update:show="handleShareDialogClose"
      :content-ids="shareDialogContent.length > 0 ? shareDialogContent : selectedContent"
      share-type="content"
      @link-generated="handleShareLinkGenerated"
      @error="handleShareError"
    />

    <!-- Upload Images Dialog -->
    <UploadImagesDialog :show="showUploadDialog" @update:show="handleUploadDialogChange" @upload-success="handleUploadSuccess" @upload-error="handleUploadError" />

    <q-dialog v-if="showVideoPlayerDialog" v-model="showVideoPlayerDialog" @hide="selectedVideo = null" maximized>
      <div class="video-player-wrapper">
        <div class="video-player-header">
          <span class="video-title">{{ selectedVideo ? selectedVideo.name || selectedVideo.filename : 'Video Player' }}</span>
          <q-btn icon="close" flat round v-close-popup />
        </div>
        <div class="video-player-content">
          <VideoPlayer v-if="selectedVideo" :src="selectedVideo.video_hls_url || selectedVideo.url" :poster="selectedVideo.preview_url" />
        </div>
      </div>
    </q-dialog>
  </div>
</template>

<script>
import { ContentApi } from '@api/index';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import VideoGenerator from './VideoGenerator.vue';
import MetaShareDialog from './meta/MetaShareDialog.vue';
import ImageFormatDialog from './ImageFormatDialog.vue';
import GalleryLightbox from './GalleryLightbox.vue';
import AddToCollectionDialog from '@components/gallery/AddToCollectionDialog.vue';
import VideoPlayer from './VideoPlayer.vue';
import DeleteConfirmationDialog from '@components/gallery/DeleteConfirmationDialog.vue';
import UploadImagesDialog from '@components/gallery/UploadImagesDialog.vue';
import AddLabelDialog from '@components/gallery/AddLabelDialog.vue';
import ShareContentDialog from '@components/gallery/ShareContentDialog.vue';

import { getContentType } from '@/utils/fileUpload';
import { debounce } from 'lodash';
import { DocumentThumbnail } from '@/components/gallery/document';
import { enhanceDocumentContentBatch } from '@/utils/documentProcessingUtils';

const ASPECT_RATIO_THRESHOLD = 1.5;
const TALL_RATIO_THRESHOLD = 0.7;

// Helper functions moved to components that need them

export default {
  name: 'ImagesGrid',
  components: {
    VideoGenerator,
    MetaShareDialog,
    ImageFormatDialog,
    GalleryLightbox,
    AddToCollectionDialog,
    VideoPlayer,
    DeleteConfirmationDialog,
    UploadImagesDialog,
    AddLabelDialog,
    ShareContentDialog,
    DocumentThumbnail
  },
  emits: ['items-removed-from-collection', 'images-deleted', 'content-updated', 'files-dropped'],
  computed: {
    validContent() {
      return this.localContent.filter(content => content && content.id);
    },

    // Enhanced content that uses processing status URLs when available
    enhancedContent() {
      // Apply processing status URLs to documents when available
      return enhanceDocumentContentBatch(this.validContent, this.documentProcessingStatuses);
    },

    showShareDialog: {
      get() {
        return !!this.selectedShareImage;
      },
      set(value) {
        if (!value) {
          this.selectedShareImage = null;
        }
      }
    },
    allSelected: {
      get() {
        return this.selectedContent.length > 0 && this.selectedContent.length === this.content.length;
      },
      set() {
        // The setter is empty because the toggleSelectAll method handles the change
      }
    },

    // Count of selected images (image content only)
    selectedImagesCount() {
      return this.selectedContent.filter(content => getContentType(content) === 'image').length;
    },

    // Count of selected videos
    selectedVideosCount() {
      return this.selectedContent.filter(content => getContentType(content) === 'video').length;
    },

    // Count of selected documents
    selectedDocumentsCount() {
      return this.selectedContent.filter(content => getContentType(content) === 'document').length;
    },

    // Drag upload store getters
    showUploadDialogFromStore() {
      return this.$store.getters['dragUpload/showUploadDialog'];
    },

    // Processing state from drag upload store
    isProcessingDrop() {
      return this.$store.getters['dragUpload/isProcessingDrop'];
    }
  },
  watch: {
    content: {
      immediate: true,
      handler(newContent, oldContent) {
        // Update local copy instead of modifying the prop directly
        this.localContent = JSON.parse(JSON.stringify(newContent || []));

        // Process the images for internal use
        if (newContent) {
          this.images = newContent.filter(item => item && item.id);
          this.checkAllContentProcessingStatus();

          // Check if we need to restore lightbox state when content updates
          // This handles cases where content is refreshed after AI editing
          if (oldContent && oldContent.length > 0) {
            this.$nextTick(() => {
              this.restoreLightboxIfNeeded();
            });
          }
        }
      }
    },
    '$route.query.editImage': {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          try {
            const imageData = JSON.parse(newValue);
            this.selectedImageForEditting = imageData;
            this.showEditorDialog = true;
          } catch (error) {
            // Silently ignore JSON parse errors - this prevents issues when URL parameters are malformed
          }
        }
      }
    },

    // Watch for changes in the drag upload store
    showUploadDialogFromStore: {
      immediate: true,
      handler(newValue) {
        this.showUploadDialog = newValue;
      }
    },

    // Watch for route changes to restore lightbox state when returning from AI editor or video generator
    '$route.path': {
      immediate: true,
      handler(newPath, oldPath) {
        // Check when navigating back to gallery from ai-editor or video-generate
        if (oldPath && (oldPath.includes('/ai-editor') || oldPath.includes('/video-generate')) && newPath === '/gallery') {
          this.$nextTick(() => {
            this.restoreLightboxIfNeeded();
          });
        }
      }
    }
  },

  props: {
    content: {
      type: Array,
      required: true,
      default: () => []
    },
    pagination: {},
    metaIntegration: {
      type: Object,
      required: false,
      default: () => {}
    },
    isMetaConnected: {
      type: Boolean,
      required: false,
      default: false
    },
    visualSearchValue: {
      type: String,
      default: ''
    },
    role: {
      type: String,
      default: '',
      required: true
    },
    selectedCollectionId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      showVideoGenerator: false,
      selectedImageForVideo: null,
      localContent: [], // Local copy of the content prop
      isNoContent: false,
      hasCheckedInitialContent: false,
      images: [],
      downloadingStates: {},
      selectedContent: [],
      downloadProgress: 0,
      showExportDialog: false,
      isExporting: false,
      exportProgress: 0,
      selectedShareImage: null,
      lightboxOpen: false,
      lightboxStartIndex: 0,
      showEditorDialog: false,
      selectedImageForEditting: null,
      showAddToCollectionDialog: false,
      isAddingToCollection: false,
      isRemovingFromCollection: false,
      showRemoveConfirmDialog: false,
      editingImageId: null,
      activeDropdownImageId: null,
      isMoreActionsMenuOpen: false,
      showVideoPlayerDialog: false,
      selectedVideo: null,
      lastClickedItemId: null,
      lastClickTime: 0,
      clickDelay: 300,
      videoProcessingStatuses: {}, // Tracks the processing status of videos
      isCheckingVideoStatus: false,
      documentProcessingStatuses: {}, // Tracks the processing status of documents
      isCheckingDocumentStatus: false,
      editingNameValue: '',
      isDragOver: false,
      dragCounter: 0, // Track drag enter/leave events
      showUploadDialog: false,
      dragLeaveTimeout: null, // Add timeout for better drag handling
      dragEnterThrottle: false, // Add throttle for drag enter events
      dragEnterThrottleTimeout: null, // Add timeout for drag enter events
      lastContentRefresh: 0, // Timestamp of last content refresh for throttling
      showShareContentDialog: false, // For share selected content dialog
      shareDialogContent: [] // Separate content holder for share dialog (to avoid UI selection)
    };
  },

  created() {
    window.addEventListener('keydown', this.handleKeyDown);

    // Initialize debounced content refresh function using Lodash
    this.debouncedRefreshContent = debounce(this.refreshContentAfterVersionChange, 500);

    // Initialize content processing status checking with TanStack Query
    this.$nextTick(() => {
      this.checkAllContentProcessingStatus();
    });

    // Add an event listener for route changes to fix menu height issues
    this.$router.beforeEach((to, from, next) => {
      // Reset any potential global styles that could be causing menu height issues
      this.$nextTick(() => {
        document.documentElement.style.setProperty('--q-menu-max-height', 'none');
      });
      next();
    });
  },

  mounted() {
    if (!this.hasCheckedInitialContent) {
      this.isNoContent = !this.content || this.content.length === 0;
      this.hasCheckedInitialContent = true;
    }

    // Check initial state for active uploads
    if (this.hasActiveUploads) {
      this.addBeforeUnloadListener();
    }

    // Listen for content deletion events from upload progress
    window.addEventListener('content-deleted', this.handleContentDeleted);

    // Listen for content upload events (fallback for when TanStack Query invalidation fails)
    window.addEventListener('content-uploaded', this.handleContentUploaded);

    // Ensure TanStack Query client is available and start initial content status check
    if (this.queryClient && this.content?.length) {
      this.checkAllContentProcessingStatus();
    }

    // Check if we need to restore lightbox state on initial mount
    this.$nextTick(() => {
      this.restoreLightboxIfNeeded();
    });
  },

  beforeDestroy() {
    // Remove keydown event listener
    window.removeEventListener('keydown', this.handleKeyDown);

    // Clean up drag timeouts
    if (this.dragLeaveTimeout) {
      clearTimeout(this.dragLeaveTimeout);
    }

    // Clean up throttle timeout
    if (this.dragEnterThrottleTimeout) {
      clearTimeout(this.dragEnterThrottleTimeout);
    }

    // Remove content deletion event listener
    window.removeEventListener('content-deleted', this.handleContentDeleted);

    // Remove content upload event listener
    window.removeEventListener('content-uploaded', this.handleContentUploaded);
  },

  setup() {
    // Get the query client for cache invalidation
    const queryClient = useQueryClient();

    // Create a mutation for updating content metadata
    const updateContentMetadataMutation = useMutation({
      mutationFn: ({ contentType, id, payload }) => {
        return ContentApi.updateImageMetadata({
          content: contentType,
          id,
          payload
        });
      },
      // Add optimistic update functionality
      onMutate: async variables => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({ queryKey: ['content'] });

        // Snapshot the previous data for potential rollback
        const previousData = queryClient.getQueryData(['content']);

        // Optimistically update the cache
        queryClient.setQueriesData({ queryKey: ['content'] }, oldData => {
          if (!oldData) return oldData;

          // Handle infinite query data structure
          if (oldData.pages) {
            return {
              ...oldData,
              pages: oldData.pages.map(page => ({
                ...page,
                content: page.content.map(item => {
                  // Update the specific content item
                  if (item.id === variables.id) {
                    return { ...item, ...variables.payload };
                  }
                  return item;
                })
              }))
            };
          }

          return oldData;
        });

        // Return context with the snapshot
        return { previousData };
      },
      // If the mutation fails, roll back to the previous data
      onError: (err, variables, context) => {
        if (context?.previousData) {
          queryClient.setQueriesData({ queryKey: ['content'] }, context.previousData);
        }
      },
      onSuccess: () => {
        // No need to invalidate the query on success since we already updated the cache optimistically
      },
      // Always refetch after error or success to ensure cache is in sync with server
      onSettled: () => {
        // Invalidate the query after the mutation completes (success or error)
        // This ensures the cache is eventually consistent with the server
        queryClient.invalidateQueries({ queryKey: ['content'] });
      }
    });

    // Create a mutation for deleting content
    const deleteContentMutation = useMutation({
      mutationFn: async contentItems => {
        const results = { success: 0, failed: 0, deletedIds: [] };

        // Delete each selected content item sequentially
        for (const content of contentItems) {
          try {
            await ContentApi.deleteImage({ content: content.type, id: content.id });
            results.success++;
            results.deletedIds.push(content.id);
          } catch (error) {
            results.failed++;
          }
        }

        return results;
      },
      onSuccess: results => {
        // Invalidate both content and collections queries
        queryClient.invalidateQueries({ queryKey: ['content'] });
        queryClient.invalidateQueries({ queryKey: ['collections'] });

        return results;
      }
    });

    // Create a mutation for removing items from collections
    const removeFromCollectionMutation = useMutation({
      mutationFn: ({ collectionId, contentIds }) => {
        const { CollectionsApi } = require('@api/modules/collections');
        return CollectionsApi.removeItemsFromCollection(collectionId, contentIds);
      },
      onSuccess: () => {
        // Invalidate both content and collections queries
        queryClient.invalidateQueries({ queryKey: ['content'] });
        queryClient.invalidateQueries({ queryKey: ['collections'] });
      }
    });

    // Create a mutation for exporting images
    const exportImagesMutation = useMutation({
      mutationFn: async ({ imageIds, formatOptions }) => {
        // Get the formatted images from the API
        const exportedImages = await ContentApi.exportImages({
          imageIds,
          formatOptions
        });

        return exportedImages;
      }
    });

    return {
      updateContentMetadataMutation,
      deleteContentMutation,
      removeFromCollectionMutation,
      exportImagesMutation,
      queryClient
    };
  },

  methods: {
    handleKeyDown(event) {
      if (event.key === 'Escape' && this.selectedContent.length > 0) {
        this.deselectAll();
      }
    },

    hideAllTooltips() {
      // Hide all tooltips in the dynamic island when any action is triggered
      const tooltipRefs = [
        'selectedImagesChipTooltip',
        'selectedVideosChipTooltip',
        'selectedDocumentsChipTooltip',
        'downloadTooltip',
        'exportTooltip',
        'shareTooltip',
        'removeCollectionTooltip',
        'addCollectionTooltip',
        'moreActionsTooltip',
        'closeTooltip',
        'selectAllTooltip'
      ];

      tooltipRefs.forEach(refName => {
        if (this.$refs[refName]) {
          this.$refs[refName].hide();
        }
      });
    },

    getContentTypeLabel(content) {
      const contentType = getContentType(content);
      switch (contentType) {
        case 'video':
          return 'Video';
        case 'document':
          return 'Document';
        default:
          return 'Image';
      }
    },

    handleMetaShare() {
      if (this.selectedContent.length === 1) {
        this.shareToMeta(this.selectedContent[0]);
      }
    },

    async handleLightboxDownload(content) {
      // Set a ref on the current lightbox component to access its loading state
      const lightboxComponent = this.$refs.galleryLightbox;
      if (lightboxComponent) {
        lightboxComponent.isDownloading = true;
      }

      try {
        await this.handleDownload(content, true);
      } finally {
        // Make sure to reset loading state whether download succeeds or fails
        if (lightboxComponent) {
          lightboxComponent.isDownloading = false;
        }
      }
    },

    isSelected(content) {
      return this.selectedContent.some(selected => selected.id === content.id);
    },

    /**
     * Smart content click handler with click delay pattern
     * Uses timing to differentiate between single and double clicks
     * Single click: Toggle selection
     * Double click: Open in lightbox/video player
     */
    handleContentClick(content) {
      if (!content) return;

      const now = Date.now();
      const isSameItem = this.lastClickedItemId === content.id;
      const isDoubleClick = isSameItem && now - this.lastClickTime < this.clickDelay;

      if (isDoubleClick) {
        // This is a double click - open in lightbox/video player
        this.handleLightboxOrVideo(content);

        // Reset click tracking to prevent triple-click issues
        this.lastClickedItemId = null;
        this.lastClickTime = 0;
      } else {
        // This is a single click - toggle selection
        this.toggleSelection(content);

        // Track this click for potential double-click detection
        this.lastClickedItemId = content.id;
        this.lastClickTime = now;
      }
    },
    toggleSelection(content) {
      if (!content) return;
      const index = this.selectedContent.findIndex(item => item.id === content.id);
      if (index === -1) {
        this.selectedContent.push(content);
      } else {
        this.selectedContent.splice(index, 1);
      }
    },

    openAddLabelDialog(content) {
      // Helper function to determine content type
      const getContentType = item => {
        if (item.type === 'video') return 'video';
        if (item.type === 'document') return 'document';
        return 'image'; // default to image
      };

      const contentType = this.selectedContent.length === 1 ? getContentType(this.selectedContent[0]) : 'items';

      if (content) {
        // If a single content item is provided, select just that one
        this.selectedContent = [content];
      }

      this.$store.dispatch('addLabelDialog/showAddLabelDialog', {
        items: this.selectedContent,
        contentType: contentType,
        onSuccess: ({ newLabel, contentItems }) => {
          // contentItems is an array of all items that were tagged
          if (Array.isArray(contentItems)) {
            // Update each content item that was tagged
            contentItems.forEach(contentItem => {
              const index = this.validContent.findIndex(c => c.id === contentItem.id);
              if (index !== -1) {
                // Set updated labels or create new labels array if needed
                this.validContent[index].labels.push(newLabel);
              }
            });
          }

          // Show success notification
          this.$q.notify({
            type: 'positive',
            message: `Tag "${newLabel}" added to ${contentItems.length} item${contentItems.length !== 1 ? 's' : ''}`,
            position: 'top'
          });
        },
        onError: ({ message }) => {
          this.$q.notify({
            type: 'negative',
            message: message || 'Failed to add tag',
            position: 'top'
          });
        }
      });
    },

    handleOpenDeleteConfirmation(singleContent) {
      if (singleContent && singleContent.id) {
        // If a single content is provided, select just that one
        this.selectedContent = [singleContent];
      }

      if (this.selectedContent.length === 0) return;

      // Determine content type for display
      const contentType = this.selectedContent.length === 1 ? (this.selectedContent[0].type === 'video' ? 'video' : 'image') : 'items';

      // Use the global delete confirmation store
      this.$store.dispatch('deleteConfirmation/showDeleteConfirmation', {
        item: this.selectedContent,
        type: 'content',
        contentCount: this.selectedContent.length,
        contentType: contentType,
        onConfirm: async contentToDelete => {
          try {
            const results = await this.deleteContentMutation.mutateAsync(contentToDelete);

            const idsToDelete = contentToDelete.map(item => item.id);
            this.$amplitude.trackEvent('Asset Deleted', {
              count: idsToDelete.length
            });

            // Check if any of the deleted content items have corresponding uploads
            const uploads = this.$store.getters['uploadProgress/uploads'];
            const uploadsToRemove = uploads.filter(upload => upload.realId && idsToDelete.includes(upload.realId));

            // Remove each matching upload from the upload progress store
            for (const upload of uploadsToRemove) {
              this.$store.dispatch('uploadProgress/removeUpload', upload.id);
            }

            this.$emit('images-deleted', idsToDelete);

            const message =
              results.failed === 0
                ? `Successfully deleted ${results.success} ${contentType}${results.success !== 1 ? 's' : ''}`
                : `Deleted ${results.success} item(s), failed to delete ${results.failed} item(s)`;

            const notificationType = results.failed === 0 ? 'positive' : 'warning';

            this.$q.notify({
              type: notificationType,
              message,
              position: 'top',
              timeout: 2000
            });

            // Clear selected content
            this.selectedContent = [];
          } catch (error) {
            console.error('Error deleting content:', error);
            this.$q.notify({
              type: 'negative',
              message: `Failed to delete content: ${error.message}`,
              position: 'top',
              timeout: 2000
            });
            throw error; // Re-throw to be handled by the store
          }
        }
      });
    },

    handleExport() {
      if (this.selectedContent.length === 0) return;
      this.showExportDialog = true;
    },

    handleShareSelectedContent() {
      if (this.selectedContent.length === 0) return;
      // Clear share dialog content so it uses selectedContent instead
      this.shareDialogContent = [];
      this.showShareContentDialog = true;
    },

    handleShareLinkGenerated() {
      // Empty handler - no notification, no auto-close
    },

    handleShareError(error) {
      this.$q.notify({
        type: 'negative',
        message: error || 'Failed to generate download link',
        position: 'top',
        timeout: 2000
      });
    },

    handleShareDialogClose(show) {
      this.showShareContentDialog = show;
      // Clear share dialog content when dialog is closed
      if (!show) {
        this.shareDialogContent = [];
      }
    },

    /**
     * Handle when a new version is added to an image stack in the lightbox
     * @param {Object} params - Object containing version information
     * @param {boolean} [params.isNewStack] - Whether this was a legacy image that just got a new stack
     */
    handleVersionAdded({ isNewStack }) {
      // Show a notification
      this.$q.notify({
        type: 'positive',
        message: 'New version added successfully',
        position: 'top',
        timeout: 2000
      });

      // Refresh the content to ensure we have the latest data
      // We need to refresh because the stack_id might have been updated
      this.refreshContentAfterVersionChange({ isLegacyImage: isNewStack });
    },

    /**
     * Handle when content processing is completed (e.g., video processing finished)
     * This ensures the gallery shows the updated content with proper URLs
     */
    handleContentProcessingCompleted() {
      // Refresh the content to ensure we have the latest processed URLs
      this.refreshContentAfterVersionChange({ avoidUrlModification: false });
    },

    /**
     * Handle refreshing content after version changes
     * This is a critical method that ensures the UI is updated after version operations
     * @param {Object} [options] - Optional parameters
     * @param {boolean} [options.isLegacyImage] - Whether this is a legacy image that just got a stack
     * @param {boolean} [options.avoidUrlModification] - Avoid modifying URLs to prevent S3 signature issues
     */
    async refreshContentAfterVersionChange(options = {}) {
      const { isLegacyImage, avoidUrlModification } = options;

      // Check if we should throttle this refresh
      if (!this.shouldRefreshContent()) {
        return;
      }

      // Update timestamp
      this.lastContentRefresh = Date.now();

      try {
        // Use the invalidateQueries method from TanStack Query to refresh data
        if (this.queryClient) {
          // Invalidate content queries to force a refresh from API
          await this.queryClient.invalidateQueries({ queryKey: ['content'] });

          // Update the lightbox if it's open
          this.$nextTick(() => this.updateLightboxContent(avoidUrlModification));

          // For legacy images, schedule an additional refresh
          if (isLegacyImage) {
            setTimeout(() => {
              this.queryClient.invalidateQueries({ queryKey: ['content'] });
            }, 1000);
          }
        } else if (typeof this.getContent === 'function') {
          // Fallback to traditional approach if queryClient is not available
          await this.getContent({ forceRefresh: true });

          // Update the lightbox if it's open
          this.$nextTick(() => this.updateLightboxContent(avoidUrlModification));

          // Handle legacy images
          if (isLegacyImage) {
            setTimeout(() => {
              if (typeof this.getContent === 'function') {
                this.getContent({ forceRefresh: true });
              }
            }, 1000);
          }
        }
      } catch (error) {
        // Silent error handling with user notification
        this.showNotification('negative', 'Could not refresh content. Please try again.');
      }
    },

    /**
     * Update the content in the lightbox component
     * @param {boolean} avoidUrlModification - Whether to avoid modifying URL properties
     */
    updateLightboxContent(avoidUrlModification = false) {
      if (!this.lightboxOpen || !this.$refs.galleryLightbox) return;

      // Update the content array using the proper method instead of directly modifying the prop
      this.$refs.galleryLightbox.updateContent([...this.enhancedContent]);

      // Then find and update the current image if it exists
      const currentImageId = this.$refs.galleryLightbox.currentImage?.id;
      if (!currentImageId) return;

      const freshImage = this.validContent.find(img => img.id === currentImageId);
      if (!freshImage) return;

      // Safe update method to avoid S3 signature issues
      if (avoidUrlModification) {
        this.updateImageSafely(this.$refs.galleryLightbox.currentImage, freshImage);
      } else {
        // Use the safe update method instead of direct assignment
        this.$refs.galleryLightbox.updateCurrentImageData(freshImage);
      }

      // Ensure the lightbox knows the image has been updated
      this.$refs.galleryLightbox.$emit('image-refreshed', freshImage);
    },

    /**
     * Update image properties without modifying the URL to prevent S3 signature issues
     * @param {Object} targetImage - The image object to update
     * @param {Object} sourceImage - The source image with new properties
     */
    updateImageSafely(targetImage, sourceImage) {
      // Create a new object with all properties
      const safeImage = JSON.parse(JSON.stringify(sourceImage));

      // Create update object, excluding URL
      const updateObj = {};

      // Copy all properties except URL
      Object.keys(safeImage).forEach(key => {
        if (key !== 'url') {
          updateObj[key] = safeImage[key];
        }
      });

      // Apply non-URL updates
      this.$refs.galleryLightbox.updateCurrentImage(updateObj);

      // Only update URL last, and only if necessary
      if (safeImage.url !== targetImage.url) {
        // Reset image loading state first
        if (this.$refs.galleryLightbox) {
          this.$refs.galleryLightbox.imageLoaded = false;
          this.$refs.galleryLightbox.imageError = false;
        }

        // Update URL separately
        this.$refs.galleryLightbox.updateCurrentImage({ url: safeImage.url });
      }
    },

    /**
     * Display a notification to the user
     * @param {string} type - Notification type (positive, negative, warning, info)
     * @param {string} message - Message to display
     * @param {number} [timeout=2000] - How long to show the notification
     */
    showNotification(type, message, timeout = 2000) {
      this.$q.notify({
        type,
        message,
        position: 'top',
        timeout
      });
    },

    /**
     * Handle when a user switches to a different version of an image
     * @param {Object} versionInfo - Object containing version information
     * @param {string|number} versionInfo.imageId - ID of the restored version
     * @param {string|number} versionInfo.originalImageId - ID of the original image
     * @param {string} versionInfo.versionName - Name of the restored version
     * @param {Object} versionInfo.versionData - Complete data about the restored version
     */
    handleVersionChanged() {
      // Use debounced refresh to prevent excessive API calls during rapid version switching
      if (this.debouncedRefreshContent) {
        this.debouncedRefreshContent({ avoidUrlModification: true });
      } else {
        // Fallback if debounced function not initialized
        this.refreshContentAfterVersionChange({ avoidUrlModification: true });
      }
    },

    /**
     * Check if content refresh is necessary based on timestamp
     */
    shouldRefreshContent() {
      const now = Date.now();
      const lastRefresh = this.lastContentRefresh || 0;
      const minInterval = 1000; // Minimum 1 second between refreshes

      return now - lastRefresh > minInterval;
    },

    /**
     * Handle when an image is updated in the lightbox (e.g., tags added/removed)
     * Ensures changes persist when closing and reopening the lightbox
     * @param {Object} updatedImage - The updated image object
     */
    handleImageUpdated(updatedImage) {
      if (!updatedImage || !updatedImage.id) return;

      // Find the image in the localContent array
      const index = this.localContent.findIndex(img => img.id === updatedImage.id);
      if (index === -1) return;

      // Create a safe copy of the updated image to avoid reactivity issues
      const safeImage = JSON.parse(JSON.stringify(updatedImage));

      // Preserve the original URL to avoid S3 signature issues
      const originalUrl = this.localContent[index].url;

      // Create a new array for Vue 2 reactivity
      const updatedLocalContent = [...this.localContent];
      // Update the specific item
      updatedLocalContent[index] = { ...safeImage, url: originalUrl };

      // Update the local data array (instead of directly mutating the prop)
      this.localContent = updatedLocalContent;
      this.$emit('content-updated', {
        id: updatedImage.id,
        changes: { ...safeImage, url: originalUrl }
      });

      // If the lightbox is open, update the lightbox content as well
      if (this.lightboxOpen && this.$refs.galleryLightbox) {
        this.$nextTick(() => {
          this.updateLightboxContent(true);
        });
      }
    },

    /**
     * Handle batch download of selected content
     * @returns {Promise<void>}
     */
    async handleBatchDownloadExisting() {
      if (this.selectedContent.length === 0) return;

      const totalItems = this.selectedContent.length;

      // Show starting notification
      this.$q.notify({
        type: 'info',
        message: `Starting download of ${totalItems} ${totalItems === 1 ? 'file' : 'files'}...`,
        position: 'top',
        timeout: 2000
      });

      try {
        // Process downloads one by one to avoid overwhelming the browser
        let completedItems = 0;
        for (const content of this.selectedContent) {
          await this.handleDownload(content, false); // Pass false to suppress individual notifications
          completedItems++;
        }

        this.$amplitude.trackEvent('Asset downloaded', {
          contentCount: completedItems
        });

        // Show success notification
        this.$q.notify({
          type: 'positive',
          message: `Successfully downloaded ${completedItems} ${completedItems === 1 ? 'file' : 'files'}`,
          position: 'top',
          timeout: 2000
        });
      } catch (error) {
        console.error('Error during batch download:', error);
        this.$q.notify({
          type: 'negative',
          message: `Error downloading files: ${error.message}`,
          position: 'top',
          timeout: 3000
        });
      }
    },

    /**
     * Handle removing selected content from a collection
     * @returns {void}
     */
    handleRemoveFromCollection() {
      if (!this.selectedCollectionId || this.selectedContent.length === 0) return;
      this.showRemoveConfirmDialog = true;
    },

    /**
     * Confirm removal of selected content from a collection
     * @returns {Promise<void>}
     */
    async confirmRemoveFromCollection() {
      if (!this.selectedCollectionId || this.selectedContent.length === 0) return;

      this.isRemovingFromCollection = true;

      try {
        await this.removeFromCollectionMutation.mutateAsync({
          collectionId: this.selectedCollectionId,
          contentIds: this.selectedContent.map(item => item.id)
        });

        // Close the dialog
        this.showRemoveConfirmDialog = false;

        // Determine content type for message
        const getContentTypeName = item => {
          if (item.type === 'video') return 'video';
          if (item.type === 'document') return 'document';
          return 'image';
        };

        const contentType = this.selectedContent.length === 1 ? getContentTypeName(this.selectedContent[0]) : 'content';

        // Notify success
        this.$q.notify({
          type: 'positive',
          message: `${this.selectedContent.length} ${contentType}${this.selectedContent.length !== 1 ? 's' : ''} removed from collection`,
          position: 'top',
          timeout: 2000
        });

        // Clear selected content
        this.selectedContent = [];

        // Emit event for backward compatibility
        this.$emit('items-removed-from-collection');
      } catch (error) {
        console.error('Error removing items from collection:', error);
        this.$q.notify({
          type: 'negative',
          message: `Failed to remove content: ${error.message}`,
          position: 'top',
          timeout: 2000
        });
      } finally {
        this.isRemovingFromCollection = false;
      }
    },

    async exportImages(formatOptions) {
      if (this.selectedContent.length === 0) return;
      this.showExportDialog = false;
      this.isExporting = true;
      this.exportProgress = 0;

      try {
        // Use the TanStack Query mutation
        const exportedImages = await this.exportImagesMutation.mutateAsync({
          imageIds: this.selectedContent.map(content => content.id),
          formatOptions
        });

        const JSZip = require('jszip');
        const zip = new JSZip();
        const totalImages = exportedImages.length;
        let completedImages = 0;

        await Promise.all(
          exportedImages.map(async image => {
            try {
              const response = await fetch(image.url, { redirect: 'follow' });
              const blob = await response.blob();
              const filename = image.url.split('/').pop().split('?')[0];
              zip.file(filename, blob);
              completedImages++;
              this.exportProgress = Math.round((completedImages / totalImages) * 100);
            } catch (error) {
              console.error(`Error downloading image ${image.id}:`, error);
            }
          })
        );

        const content = await zip.generateAsync({ type: 'blob' });
        const url = window.URL.createObjectURL(content);
        const a = document.createElement('a');
        a.href = url;
        a.download = `formatted-images-${new Date().getTime()}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        console.error('Error in batch format and download:', error);
        this.$q.notify({
          type: 'negative',
          message: `Failed to export content: ${error.message}`,
          position: 'top',
          timeout: 2000
        });
      } finally {
        this.isExporting = false;
        this.exportProgress = 0;
        this.$amplitude.trackEvent('Batch export Initiated', {
          contentCount: this.selectedContent.length,
          formatOptions: JSON.stringify(formatOptions)
        });
      }
    },

    getImageRatio(content) {
      // If aspect_ratio is directly available, use it
      if (content.aspect_ratio) {
        return content.aspect_ratio;
      }

      // If both width and height are available, calculate ratio
      if (content.width && content.height) {
        return content.width / content.height;
      }

      // For video content with no ratio info, use a default landscape ratio
      if (content.type === 'video') {
        return 16 / 9; // Standard video aspect ratio
      }

      // Default fallback for unrecognized content
      return 1; // Square ratio as default
    },

    getImageClass(content) {
      let gridClass = '';

      // Determine grid size class based on aspect ratio
      if (content.type === 'video') {
        const aspectRatio = this.getImageRatio(content);
        if (aspectRatio > ASPECT_RATIO_THRESHOLD) {
          gridClass = 'grid-item--landscape grid-item--video';
        } else if (aspectRatio < TALL_RATIO_THRESHOLD) {
          gridClass = 'grid-item--portrait grid-item--video';
        } else {
          gridClass = 'grid-item--video';
        }
      } else {
        const aspectRatio = this.getImageRatio(content);
        if (aspectRatio > ASPECT_RATIO_THRESHOLD) {
          gridClass = 'grid-item--landscape';
        } else if (aspectRatio < TALL_RATIO_THRESHOLD) {
          gridClass = 'grid-item--portrait';
        }
      }

      return gridClass;
    },

    // Content type utility
    getContentType(content) {
      return getContentType(content);
    },

    async handleDownload(content, showNotifications = true) {
      this.$set(this.downloadingStates, content.id, true);
      try {
        let downloadUrl;

        // Find the original content data (before enhancement) to get the original URL
        const originalContent = this.validContent.find(item => item.id === content.id);

        // Get the correct download URL based on content type
        if (content.type === 'document') {
          // For documents, always use the original document URL for downloads
          downloadUrl = originalContent ? originalContent.url : content.url;
        } else {
          // For images and videos, use the regular URL
          downloadUrl = content.url;
        }
        if (!downloadUrl) {
          throw new Error('Download URL not available');
        }

        const response = await fetch(downloadUrl, { redirect: 'follow' });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = content.name || content.filename || content.url.split('/').pop().split('?')[0];
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        return true;
      } catch (error) {
        console.error(`Error downloading ${content.type}:`, error);
        if (showNotifications) {
          this.$q.notify({
            type: 'negative',
            message: `Failed to download ${content.type}. Please try again.`,
            position: 'top',
            timeout: 2000
          });
        }
        throw error;
      } finally {
        this.$set(this.downloadingStates, content.id, false);
      }
    },

    handleCopyLink(content) {
      // Use separate content holder to avoid visual selection in UI
      this.shareDialogContent = [content];
      this.showShareContentDialog = true;
    },

    shareToMeta(image) {
      if (!this.isMetaConnected) {
        this.$router.push('/integrations');
        return;
      }
      this.selectedShareImage = image;
    },

    // handleMetaShare method is now consolidated in one place above
    toggleSelectAll() {
      if (this.selectedContent.length === this.enhancedContent.length) {
        // Deselect all
        this.selectedContent = [];
      } else {
        // Select all
        this.selectedContent = [...this.enhancedContent];
      }
    },

    openLightbox(index) {
      this.lightboxStartIndex = index;
      this.lightboxOpen = true;
    },
    openEditor(image, isFromLightbox = false) {
      if (isFromLightbox && this.lightboxOpen) {
        // Store lightbox state for restoration after editing
        const lightboxState = {
          isFromLightbox: true,
          contentIndex: this.lightboxStartIndex,
          currentImageId: image.id,
          allContent: [...this.validContent] // Store a copy of current content
        };

        // Store the image and lightbox state in the images-editor store
        this.$store.dispatch('imagesEditor/openImagesEditorFromLightbox', {
          image,
          lightboxState
        });
      } else {
        // Normal editor opening from grid
        this.$store.dispatch('imagesEditor/openImagesEditor', image);
      }

      // Navigate to gallery/ai-editor route
      this.$router.push('/gallery/ai-editor').catch(err => {
        // Only log errors that aren't navigation duplications
        if (err.name !== 'NavigationDuplicated') {
          console.error('Navigation error:', err);
        }
      });
    },
    closeEditor() {
      this.imageToEdit = null;
      this.showImageEditor = false;
    },
    openVideoGenerator(image, isFromLightbox = false) {
      if (isFromLightbox && this.lightboxOpen) {
        // Store lightbox state for restoration after video generation
        const lightboxState = {
          isFromLightbox: true,
          contentIndex: this.lightboxStartIndex,
          currentImageId: image.id,
          allContent: [...this.validContent] // Store a copy of current content
        };

        // Store the image and lightbox state in the video generator store
        this.$store.dispatch('videoGenerator/openVideoGeneratorFromLightbox', {
          image,
          lightboxState
        });
      } else {
        // Normal video generator opening from grid
        this.$store.dispatch('videoGenerator/openVideoGenerator', image);
      }

      // Navigate to gallery/video-generate route without query parameters
      this.$router.push('/gallery/video-generate').catch(err => {
        // Only log errors that aren't navigation duplications
        if (err.name !== 'NavigationDuplicated') {
          console.error('Navigation error:', err);
        }
      });
    },
    handleVideoGeneratorClose(isOpen) {
      if (!isOpen) {
        this.showVideoGenerator = false;
        this.selectedImageForVideo = null;
      }
    },
    openAddToCollectionDialog() {
      this.showAddToCollectionDialog = true;
    },
    handleAddToCollectionSuccess(message) {
      this.isAddingToCollection = false;
      this.$q.notify({
        type: 'positive',
        message: message,
        position: 'top',
        timeout: 2000
      });
      this.selectedContent = [];
    },
    deselectAll() {
      this.selectedContent = [];
    },
    startEditingName(image) {
      this.editingImageId = image.id;
      this.editingNameValue = image.name || image.filename;
      // Focus on the input field after a small delay to ensure it's rendered
      this.$nextTick(() => {
        const editField = this.$refs.nameEditField;
        if (editField) {
          let targetElement;

          if (Array.isArray(editField)) {
            // If it's an array of refs, select the last one which should be the current one
            targetElement = editField[editField.length - 1];
          } else {
            // If it's a single element
            targetElement = editField;
          }

          if (targetElement) {
            // Focus and select all text in the input field
            targetElement.focus();
            targetElement.select();
          }
        }
      });
    },
    handleInputBlur(content) {
      if (this.editingImageId !== content.id) return;
      this.saveImageName(content);
    },
    cancelEditing() {
      this.editingImageId = null;
      this.editingNameValue = '';
    },
    async saveImageName(content) {
      // Get the trimmed new name from our v-model
      const newName = this.editingNameValue.trim();

      // Store the current editing ID
      const currentEditingId = this.editingImageId;
      const hasExtension = newName.includes('.') && newName.lastIndexOf('.') > 0;
      let finalName = newName;

      if (!hasExtension && content.filename) {
        const extension = this.getFileExtension(content.filename);
        if (extension) {
          finalName = newName + '.' + extension.toLowerCase();
        }
      }

      // Early returns for invalid cases - clear editing state
      if (!currentEditingId) {
        this.editingImageId = null;
        return;
      }

      // Don't update if name is empty
      if (!finalName) {
        this.editingImageId = null;
        this.editingNameValue = '';
        return;
      }

      // Don't update if name is unchanged
      if (finalName === content.name) {
        this.editingImageId = null;
        this.editingNameValue = '';
        return;
      }

      // Immediately clear editing state - the UI will update optimistically
      this.editingImageId = null;
      this.editingNameValue = '';

      // Determine content type for messages
      const contentType = getContentType(content);

      try {
        // Use the TanStack Query mutation with optimistic update
        await this.updateContentMetadataMutation.mutateAsync({
          contentType: contentType === 'video' ? 'video' : 'image',
          id: content.id,
          payload: { name: finalName }
        });

        this.$q.notify({
          type: 'positive',
          message: `${contentType.charAt(0).toUpperCase() + contentType.slice(1)} name updated`,
          position: 'top',
          timeout: 1500
        });
      } catch (error) {
        // Error handling is done by the mutation's onError
        this.$q.notify({
          type: 'negative',
          message: `Failed to update ${contentType} name`,
          position: 'top',
          timeout: 2000
        });
      }
    },
    formatDuration(seconds) {
      if (!seconds) return '0:00';
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    },

    // Extract file extension from filename
    getFileExtension(filename) {
      if (!filename) return 'Unknown';
      const parts = filename.split('.');
      if (parts.length > 1) {
        const extension = parts[parts.length - 1].toUpperCase();
        return extension;
      }
      return 'Unknown';
    },

    playVideo(video) {
      // Check if the video is still processing
      const status = this.videoProcessingStatuses[video.id];
      if (status && (status === 'pending' || status === 'processing')) {
        this.$q.notify({
          type: 'warning',
          message: `This video is still ${status}. Please wait until processing is complete.`,
          position: 'top',
          timeout: 3000
        });
        return;
      }

      if (status === 'failed') {
        this.$q.notify({
          type: 'negative',
          message: 'This video failed to process and cannot be played.',
          position: 'top',
          timeout: 3000
        });
        return;
      }

      this.selectedVideo = video;
      this.showVideoPlayerDialog = true;
    },

    /**
     * Check if a video is currently in processing state
     * @param {Object} content - The content item to check
     * @returns {Boolean} True if the video is still processing and cannot be interacted with
     */
    isVideoProcessing(content) {
      if (!content || content.type !== 'video') return false;

      const status = this.videoProcessingStatuses[content.id];
      return status && status !== 'finished';
    },

    /**
     * Check if a document is currently in processing state
     * @param {Object} content - The content item to check
     * @returns {Boolean} True if the document is still processing and cannot be interacted with
     */
    isDocumentProcessing(content) {
      if (!content || getContentType(content) !== 'document') return false;

      // Check if upload is still in progress (show processing overlay)
      if (this.$store && this.$store.getters) {
        const uploads = this.$store.getters['uploadProgress/uploads'] || [];
        const activeUpload = uploads.find(upload => upload.realId === content.id && upload.progress < 100);
        if (activeUpload) {
          return true; // Show processing overlay during upload
        }
      }

      return false;
    },

    /**
     * Check the processing status of videos in the content list using TanStack Query
     * This prevents users from trying to play videos that aren't ready yet
     */
    async checkVideoProcessingStatus() {
      // Early returns for efficiency
      if (!this.content?.length || this.isCheckingVideoStatus) return;

      // Extract videos that need status checking
      const { videoContents, videoIds } = this.extractVideosForStatusCheck();
      if (!videoIds.length) return;

      this.isCheckingVideoStatus = true;

      try {
        // Use TanStack Query to fetch status with proper caching
        const statusData = await this.queryClient.fetchQuery({
          queryKey: ['video-processing-status', videoIds],
          queryFn: () => ContentApi.getVideosProcessingStatus(videoIds),
          staleTime: 1000 * 2, // 2 seconds
          cacheTime: 1000 * 5, // 5 seconds
          retry: 1 // Only retry once on failure
        });

        if (statusData.statusCode === 200 && statusData.data?.videos?.length) {
          const { hasIncompleteVideos } = this.processVideoStatusUpdates(statusData.data.videos, videoContents);

          // Schedule next check if needed with exponential backoff
          if (hasIncompleteVideos) {
            setTimeout(() => this.checkVideoProcessingStatus(), 4000);
          }
        }
      } catch (error) {
        console.error('Error checking video processing status:', error);
        // Continue checking but with longer delay on error
        setTimeout(() => this.checkVideoProcessingStatus(), 8000);
      } finally {
        this.isCheckingVideoStatus = false;
      }
    },

    /**
     * Extract video content items that need status checking
     * @returns {Object} Object containing videoContents and videoIds
     */
    extractVideosForStatusCheck() {
      const videoContents = {};
      const videoIds = [];

      this.content.forEach(item => {
        if (item?.type === 'video' && (item.processing_status === 'pending' || item.processing_status === 'processing')) {
          videoContents[item.id] = item;
          videoIds.push(item.id);
        }
      });

      return { videoContents, videoIds };
    },

    /**
     * Process video status updates from the API
     * @param {Array} videos - Array of video status information from API
     * @param {Object} videoContents - Map of video content items by ID
     * @returns {Object} Object with hasIncompleteVideos flag
     */
    processVideoStatusUpdates(videos, videoContents) {
      const newStatuses = {};
      let hasIncompleteVideos = false;
      let hasNewlyFinishedVideos = false;

      // Process all updates in a single loop for better performance
      videos.forEach(video => {
        // Check if this video just finished processing
        const wasProcessing = this.videoProcessingStatuses[video.id] && this.videoProcessingStatuses[video.id] !== 'finished';
        const isNowFinished = video.processing_status === 'finished';

        if (wasProcessing && isNowFinished) {
          hasNewlyFinishedVideos = true;
        }

        // Update processing status
        newStatuses[video.id] = video.processing_status;

        // Check for incomplete videos
        if (video.processing_status === 'pending' || video.processing_status === 'processing') {
          hasIncompleteVideos = true;
        }
        // Update URLs if video is finished
        else if (video.processing_status === 'finished') {
          this.updateFinishedVideoUrls(video, videoContents[video.id]);
        }
      });

      // Batch update all statuses at once for better performance
      this.videoProcessingStatuses = {
        ...this.videoProcessingStatuses,
        ...newStatuses
      };

      // Clean up processing statuses for finished videos after a delay
      // This prevents UI confusion and reduces memory usage
      setTimeout(() => {
        const cleanedStatuses = { ...this.videoProcessingStatuses };
        Object.keys(cleanedStatuses).forEach(videoId => {
          if (cleanedStatuses[videoId] === 'finished' || cleanedStatuses[videoId] === 'failed') {
            delete cleanedStatuses[videoId];
          }
        });
        this.videoProcessingStatuses = cleanedStatuses;
      }, 5000); // Clean up after 5 seconds

      // If we have newly finished videos, invalidate queries after a short delay
      // to ensure the server has fully processed the updates
      if (hasNewlyFinishedVideos && this.queryClient) {
        // First, update the cache optimistically
        setTimeout(() => {
          this.queryClient.invalidateQueries({ queryKey: ['content'] });
          // Also invalidate video processing status cache to prevent stale data
          this.queryClient.removeQueries({ queryKey: ['video-processing-status'] });
        }, 2000);
      }

      return { hasIncompleteVideos };
    },

    /**
     * Update URLs for a video that has finished processing
     * @param {Object} video - Video information from API
     * @param {Object} contentItem - The content item to update
     */
    updateFinishedVideoUrls(video, contentItem) {
      if (!contentItem) return;

      // Update properties if they exist in the response
      const urlProps = ['placeholder_url', 'preview_url', 'video_hls_url'];

      urlProps.forEach(prop => {
        if (video[prop]) {
          const targetProp = prop === 'video_hls_url' ? 'url' : prop;
          contentItem[targetProp] = this.ensureHttps(video[prop]);
        }
      });

      // Update the local content array to trigger reactivity
      const localIndex = this.localContent.findIndex(item => item.id === video.id);
      if (localIndex !== -1) {
        // Create a new object to ensure Vue reactivity
        const updatedContent = { ...this.localContent[localIndex] };

        urlProps.forEach(prop => {
          if (video[prop]) {
            const targetProp = prop === 'video_hls_url' ? 'url' : prop;
            updatedContent[targetProp] = this.ensureHttps(video[prop]);
          }
        });

        // Update the local content array
        this.$set(this.localContent, localIndex, updatedContent);
      }

      // Update TanStack Query cache optimistically
      if (this.queryClient) {
        this.queryClient.setQueriesData({ queryKey: ['content'] }, oldData => {
          if (!oldData) return oldData;

          // Handle infinite query data structure
          if (oldData.pages) {
            return {
              ...oldData,
              pages: oldData.pages.map(page => ({
                ...page,
                content: page.content.map(item => {
                  if (item.id === video.id) {
                    const updatedItem = { ...item };
                    urlProps.forEach(prop => {
                      if (video[prop]) {
                        const targetProp = prop === 'video_hls_url' ? 'url' : prop;
                        updatedItem[targetProp] = this.ensureHttps(video[prop]);
                      }
                    });
                    return updatedItem;
                  }
                  return item;
                })
              }))
            };
          }

          // Handle regular array data structure
          if (Array.isArray(oldData.content)) {
            return {
              ...oldData,
              content: oldData.content.map(item => {
                if (item.id === video.id) {
                  const updatedItem = { ...item };
                  urlProps.forEach(prop => {
                    if (video[prop]) {
                      const targetProp = prop === 'video_hls_url' ? 'url' : prop;
                      updatedItem[targetProp] = this.ensureHttps(video[prop]);
                    }
                  });
                  return updatedItem;
                }
                return item;
              })
            };
          }

          return oldData;
        });
      }
    },

    /**
     * Smart handler for the "Open in Lightbox" option
     * - Now opens both images and videos in the content-agnostic lightbox
     */
    handleLightboxOrVideo(content) {
      // Open both images and videos in the lightbox since it's now content-agnostic
      this.openLightbox(this.enhancedContent.indexOf(content));
    },
    handleDeleteDialogHide() {
      if (this.selectedContent.length === 0) {
        this.showDeleteConfirmDialog = false;
      }
    },
    /**
     * Ensure a URL has https:// prefix
     * @param {string} url - URL to process
     * @returns {string} URL with https:// prefix
     */
    ensureHttps(url) {
      if (!url) return url;
      return url.startsWith('http') ? url : `https://${url}`;
    },

    // Generic Content Processing Methods (Step 1: Reduce duplication)
    /**
     * Check if any content is currently processing (videos or documents)
     * @param {Object} content - Content object
     * @returns {boolean} True if content is processing
     */
    isAnyContentProcessing(content) {
      if (this.isVideoProcessing(content)) return true;
      if (this.isDocumentProcessing(content)) return true;
      return false;
    },

    /**
     * Check processing status for all content types (videos and documents)
     * This reduces duplication by handling both types in one method
     */
    async checkAllContentProcessingStatus() {
      // Check both video and document processing in parallel
      const videoCheck = this.checkVideoProcessingStatus();
      const documentCheck = this.checkDocumentProcessingStatus();

      // Wait for both to complete
      await Promise.all([videoCheck, documentCheck]);
    },

    /**
     * Get the appropriate processing status display text for content
     * @param {Object} content - Content object
     * @returns {string} Processing status text to display
     */
    getProcessingStatusText(content) {
      const contentType = getContentType(content);
      if (contentType === 'video') {
        return this.videoProcessingStatuses[content.id] || content.processing_status || 'processing';
      } else if (contentType === 'document') {
        // Always show "processing" for documents since they return "completed" even when processing
        return 'processing';
      }
      return 'processing';
    },

    // Drag and Drop Methods
    isFileDrag(e) {
      // Check if the drag event contains files (not text or other elements)
      return e.dataTransfer && e.dataTransfer.types && e.dataTransfer.types.includes('Files');
    },

    handleDragEnter(e) {
      e.preventDefault();
      e.stopPropagation();

      // Check if drag and drop is enabled
      if (!this.$store.getters['dragUpload/isDragDropEnabled']) {
        return;
      }

      // Only count drag enters from external file sources, and throttle
      if (this.isFileDrag(e)) {
        // Throttle drag enter to avoid excessive processing
        if (this.dragEnterThrottle) return;

        this.dragCounter++;
        if (this.dragCounter === 1) {
          this.isDragOver = true;
        }

        // Simple throttling - prevent processing for 50ms
        this.dragEnterThrottle = true;
        this.dragEnterThrottleTimeout = setTimeout(() => {
          this.dragEnterThrottle = false;
        }, 50);
      }
    },

    handleDragOver(e) {
      e.preventDefault();
      e.stopPropagation();

      // Check if drag and drop is enabled
      if (!this.$store.getters['dragUpload/isDragDropEnabled']) {
        return;
      }

      // Only process file drags, and throttle the expensive operations
      if (!this.isFileDrag(e)) return;

      // Clear any pending leave timeout since we're still dragging
      if (this.dragLeaveTimeout) {
        clearTimeout(this.dragLeaveTimeout);
        this.dragLeaveTimeout = null;
      }

      // Ensure overlay remains visible during drag over (simplified)
      if (!this.isDragOver) {
        this.isDragOver = true;
        this.dragCounter = 1;
      }
    },

    handleDragLeave(e) {
      e.preventDefault();
      e.stopPropagation();

      // Check if drag and drop is enabled
      if (!this.$store.getters['dragUpload/isDragDropEnabled']) {
        return;
      }

      // Clear any existing timeout
      if (this.dragLeaveTimeout) {
        clearTimeout(this.dragLeaveTimeout);
      }

      // Add a small delay to prevent flashing when moving between child elements
      this.dragLeaveTimeout = setTimeout(() => {
        // Only count drag leaves that are actually leaving the container
        // Check if we're leaving the main container, not just moving between child elements
        if (!this.$el.contains(e.relatedTarget)) {
          this.dragCounter--;
          if (this.dragCounter <= 0) {
            this.dragCounter = 0;
            this.isDragOver = false;
          }
        }
      }, 50); // Small delay to prevent rapid flickering
    },

    async handleDrop(e) {
      e.preventDefault();
      e.stopPropagation();

      // Check if drag and drop is enabled
      if (!this.$store.getters['dragUpload/isDragDropEnabled']) {
        return;
      }

      // Clean up drag state immediately for responsive UI
      if (this.dragLeaveTimeout) {
        clearTimeout(this.dragLeaveTimeout);
        this.dragLeaveTimeout = null;
      }
      this.dragCounter = 0;
      this.isDragOver = false;

      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) return;

      // Quick filter for supported types (fast operation)
      const supportedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'video/mp4',
        'video/quicktime',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      const validFiles = files.filter(file => supportedTypes.includes(file.type));

      if (validFiles.length === 0) {
        this.$q.notify({
          type: 'warning',
          message: 'Please drop supported file types only: JPEG, JPG, PNG, MP4, MOV, PDF, DOC, or DOCX files',
          position: 'top',
          timeout: 3000
        });
        return;
      }

      try {
        // Move all heavy processing to the store action
        await this.$store.dispatch('dragUpload/handleFileDrop', {
          files: validFiles,
          source: 'drag-drop'
        });

        // Simple success notification
        this.$q.notify({
          type: 'positive',
          message: `${validFiles.length} file${validFiles.length !== 1 ? 's' : ''} ready for upload`,
          position: 'top',
          timeout: 2000
        });
      } catch (error) {
        console.error('Error processing dropped files:', error);
        this.$q.notify({
          type: 'negative',
          message: 'Error processing files. Please try again.',
          position: 'top',
          timeout: 3000
        });
      }
    },

    // Delete function to pass to GlobalUploadProgress
    async handleUploadDelete(upload) {
      return new Promise(resolve => {
        this.$q
          .dialog({
            title: 'Delete Content',
            message: `Are you sure you want to delete "${upload.fileName}"? This action cannot be undone.`,
            persistent: true,
            ok: {
              label: 'Delete',
              color: 'negative',
              'no-caps': true
            },
            cancel: {
              label: 'Cancel',
              color: 'grey-7',
              'no-caps': true,
              flat: true
            }
          })
          .onOk(async () => {
            try {
              // Call the store's delete action
              const result = await this.$store.dispatch('uploadProgress/deleteUpload', {
                uploadId: upload.id,
                realId: upload.realId,
                contentType: upload.contentType
              });

              if (result.success) {
                this.$q.notify({
                  type: 'positive',
                  message: `"${upload.fileName}" deleted successfully`,
                  position: 'top',
                  timeout: 2000
                });
                resolve(true);
              } else {
                throw new Error(result.error);
              }
            } catch (error) {
              console.error('Error deleting upload:', error);
              this.$q.notify({
                type: 'negative',
                message: `Failed to delete "${upload.fileName}": ${error.message}`,
                position: 'top',
                timeout: 3000
              });
              resolve(false);
            }
          })
          .onCancel(() => {
            resolve(false);
          });
      });
    },
    handleUploadDialogChange(show) {
      this.showUploadDialog = show;
      // If dialog is being closed, notify the store
      if (!show) {
        this.$store.dispatch('dragUpload/closeUploadDialog');
      }
    },
    handleUploadSuccess(data) {
      // Handle both string messages (legacy) and object data (new format)
      let message;

      if (typeof data === 'string') {
        message = data;
      } else if (data && typeof data === 'object') {
        const { count, collectionName, partialSuccess, errorMessage } = data;

        if (partialSuccess && errorMessage) {
          // Handle partial success with error
          message = `${count} file${count !== 1 ? 's' : ''} uploaded to ${collectionName}, but ${errorMessage}`;
        } else {
          // Handle normal success
          const locationText = collectionName && collectionName !== 'All Assets' ? ` to ${collectionName}` : '';
          message = `${count} file${count !== 1 ? 's' : ''} uploaded successfully${locationText}`;
        }
      } else {
        message = 'Upload completed successfully';
      }

      if (this.isNoContent) {
        this.$amplitude.trackEvent('Assets Uploaded (Post-Onboarding)');
      }

      this.$q.notify({
        type: 'positive',
        message: message,
        position: 'top',
        timeout: 2000
      });
    },
    handleUploadError(error) {
      this.$q.notify({
        type: 'negative',
        message: error,
        position: 'top',
        timeout: 2000
      });
    },

    openUploadDialog() {
      // Use the drag-upload store to open the upload dialog
      this.$store.dispatch('uploadProgress/showDialog');
    },

    // Handle content deletion events from GlobalUploadProgress
    handleContentDeleted(event) {
      const { contentId } = event.detail;

      // Remove the deleted content from local data
      const index = this.localContent.findIndex(item => item.id === contentId);
      if (index !== -1) {
        // Create a new array to trigger reactivity
        const updatedContent = [...this.localContent];
        updatedContent.splice(index, 1);
        this.localContent = updatedContent;

        // Also remove from selected content if it was selected
        this.selectedContent = this.selectedContent.filter(item => item.id !== contentId);

        // Emit event for parent components
        this.$emit('images-deleted', [contentId]);
      }

      // Force TanStack Query to refetch as well
      if (this.queryClient) {
        this.queryClient.invalidateQueries({ queryKey: ['content'] });
      }
    },

    // Handle content upload events (fallback for when TanStack Query invalidation fails)
    handleContentUploaded(event) {
      const { contentId, contentType } = event.detail;
      // Use TanStack Query to invalidate and refetch content
      if (this.queryClient) {
        // Invalidate content queries to force refresh
        this.queryClient.invalidateQueries({ queryKey: ['content'] });
        this.queryClient.invalidateQueries({ queryKey: ['collections'] });
      }

      // Emit event to parent component as well
      this.$emit('content-updated', {
        id: contentId,
        type: contentType,
        action: 'uploaded'
      });
    },

    /**
     * Restore lightbox state when returning from AI editor or video generator
     * This ensures users return to the lightbox view they started from
     */
    restoreLightboxIfNeeded() {
      // Only restore lightbox if we're actually on the gallery route
      // This prevents restoration when refreshing the editor or video generator pages
      if (this.$route.path !== '/gallery') {
        return;
      }

      // Check if we should restore lightbox state from AI editor
      const editorLightboxState = this.$store.getters['imagesEditor/getLightboxState'];
      const isFromEditorLightbox = this.$store.getters['imagesEditor/getIsFromLightbox'];

      // Check if we should restore lightbox state from video generator
      const videoLightboxState = this.$store.getters['videoGenerator/getLightboxState'];
      const isFromVideoLightbox = this.$store.getters['videoGenerator/getIsFromLightbox'];

      // Determine which state to use (prioritize the most recent one)
      let lightboxState = null;
      let storeModule = null;

      if (isFromEditorLightbox && editorLightboxState) {
        lightboxState = editorLightboxState;
        storeModule = 'imagesEditor';
      } else if (isFromVideoLightbox && videoLightboxState) {
        lightboxState = videoLightboxState;
        storeModule = 'videoGenerator';
      }

      if (!lightboxState || this.lightboxOpen) {
        return; // No restoration needed or lightbox already open
      }

      try {
        // Find the current index of the image that was being processed
        let targetIndex = lightboxState.contentIndex;

        // Try to find the image by ID in case the content order changed
        if (lightboxState.currentImageId) {
          const currentImageIndex = this.enhancedContent.findIndex(item => item.id === lightboxState.currentImageId);
          if (currentImageIndex !== -1) {
            targetIndex = currentImageIndex;
          }
        }

        // Ensure the target index is within bounds
        if (targetIndex >= 0 && targetIndex < this.enhancedContent.length) {
          // Restore the lightbox state
          this.lightboxStartIndex = targetIndex;
          this.lightboxOpen = true;

          // Clear the lightbox state from the appropriate store to prevent multiple restorations
          this.$store.dispatch(`${storeModule}/setLightboxState`, null);
        } else {
          // If we can't find the exact image, open lightbox at first image
          if (this.enhancedContent.length > 0) {
            this.lightboxStartIndex = 0;
            this.lightboxOpen = true;
            this.$store.dispatch(`${storeModule}/setLightboxState`, null);
            console.log(`Lightbox restored from ${storeModule} at first image due to index mismatch`);
          }
        }
      } catch (error) {
        console.error('Error restoring lightbox state:', error);
        // Clear invalid state from both stores
        this.$store.dispatch('imagesEditor/setLightboxState', null);
        this.$store.dispatch('videoGenerator/setLightboxState', null);
      }
    },

    // Document Processing Status Methods

    /**
     * Check the processing status of documents in the content list using TanStack Query
     * This updates thumbnails when documents finish processing
     */
    async checkDocumentProcessingStatus() {
      // Early returns for efficiency
      if (!this.content?.length || this.isCheckingDocumentStatus) {
        return;
      }

      // Extract documents that need status checking
      const { documentContents, documentIds } = this.extractDocumentsForStatusCheck();

      if (!documentIds.length) {
        return;
      }

      this.isCheckingDocumentStatus = true;

      try {
        // Use TanStack Query to fetch status with proper caching
        const statusData = await this.queryClient.fetchQuery({
          queryKey: ['document-processing-status', documentIds],
          queryFn: () => ContentApi.getContentProcessingStatus({ contentIds: documentIds, type: 'document' }),
          staleTime: 1000 * 2, // 2 seconds
          cacheTime: 1000 * 5, // 5 seconds
          retry: 1 // Only retry once on failure
        });

        if (statusData.statusCode === 200 && statusData.data?.length) {
          // Extract raw data from Vue observer to avoid reactivity issues
          const rawData = JSON.parse(JSON.stringify(statusData.data));
          const { hasIncompleteDocuments } = this.processDocumentStatusUpdates(rawData, documentContents);

          // Schedule next check if needed with exponential backoff
          if (hasIncompleteDocuments) {
            setTimeout(() => this.checkDocumentProcessingStatus(), 4000);
          }
        }
      } catch (error) {
        console.error('Error checking document processing status:', error);
        // Continue checking but with longer delay on error
        setTimeout(() => this.checkDocumentProcessingStatus(), 8000);
      } finally {
        this.isCheckingDocumentStatus = false;
      }
    },

    /**
     * Extract document content items that need status checking
     * @returns {Object} Object containing documentContents and documentIds
     */
    extractDocumentsForStatusCheck() {
      const documentContents = {};
      const documentIds = [];

      this.content.forEach(item => {
        if (getContentType(item) === 'document') {
          // Always track documents to check for process_document state with placeholder_url
          // The placeholder_url is available when state="process_document" even if status="completed"
          documentContents[item.id] = item;
          documentIds.push(item.id);
        }
      });

      return { documentContents, documentIds };
    },

    /**
     * Process document status updates from the API
     * @param {Array} documents - Array of document status information from API
     * @param {Object} documentContents - Map of document content items by ID
     * @returns {Object} Object with hasIncompleteDocuments flag
     */
    processDocumentStatusUpdates(documents, documentContents) {
      const newStatuses = {};
      let hasIncompleteDocuments = false;
      let hasNewlyFinishedDocuments = false;

      // Process all updates in a single loop for better performance
      documents.forEach(document => {
        const processingStatus = document.status;

        // Check if this document just finished processing
        const currentData = this.documentProcessingStatuses[document.id];
        const wasProcessing = currentData && currentData.status !== 'completed';
        const isNowCompleted = processingStatus === 'completed';

        if (wasProcessing && isNowCompleted) {
          hasNewlyFinishedDocuments = true;
        }

        // Store full processing data (like videos) - including temporary URLs
        const statusData = {
          status: processingStatus,
          placeholder_url: document.placeholder_url,
          optimized_url: document.optimized_url,
          progress: document.progress || (processingStatus === 'completed' ? 100 : 0),
          message: document.message || '',
          state: document.state || processingStatus
        };

        newStatuses[document.id] = statusData;

        // Check for incomplete documents (documents that haven't reached process_document state)
        if (document.state !== 'process_document') {
          hasIncompleteDocuments = true;
        }
        // Update URLs if document processing is finished
        else if (processingStatus === 'completed') {
          this.updateFinishedDocumentUrls(document, documentContents[document.id]);
        }
      });

      // Batch update all statuses at once for better performance
      this.documentProcessingStatuses = {
        ...this.documentProcessingStatuses,
        ...newStatuses
      };

      // Clean up processing statuses for finished documents after a delay
      // This prevents UI confusion and reduces memory usage
      setTimeout(() => {
        const cleanedStatuses = { ...this.documentProcessingStatuses };
        Object.keys(cleanedStatuses).forEach(documentId => {
          const statusData = cleanedStatuses[documentId];
          // Only clean up failed documents, keep completed ones with URLs
          if (statusData && statusData.status === 'failed') {
            delete cleanedStatuses[documentId];
          }
        });
        this.documentProcessingStatuses = cleanedStatuses;
      }, 10000); // Clean up failed documents after 10 seconds

      // If we have newly finished documents, invalidate queries after a short delay
      // to ensure the server has fully processed the updates
      if (hasNewlyFinishedDocuments && this.queryClient) {
        // First, update the cache optimistically
        setTimeout(() => {
          this.queryClient.invalidateQueries({ queryKey: ['content'] });
          // Also invalidate document processing status cache to prevent stale data
          this.queryClient.removeQueries({ queryKey: ['document-processing-status'] });
        }, 2000);
      }

      return { hasIncompleteDocuments };
    },

    /**
     * Update URLs for a document that has finished processing
     * @param {Object} document - Document information from API
     * @param {Object} contentItem - The content item to update
     */
    updateFinishedDocumentUrls(document, contentItem) {
      if (!contentItem) return;

      // Update properties if they exist in the response
      const urlProps = ['placeholder_url', 'preview_url'];

      urlProps.forEach(prop => {
        if (document[prop]) {
          contentItem[prop] = this.ensureHttps(document[prop]);
        }
      });

      // Update the local content array to trigger reactivity
      const localIndex = this.localContent.findIndex(item => item.id === document.id);
      if (localIndex !== -1) {
        // Create a new object to ensure Vue reactivity
        const updatedContent = { ...this.localContent[localIndex] };

        urlProps.forEach(prop => {
          if (document[prop]) {
            updatedContent[prop] = this.ensureHttps(document[prop]);
          }
        });

        // Update the local content array
        this.$set(this.localContent, localIndex, updatedContent);
      }

      // Update TanStack Query cache optimistically
      if (this.queryClient) {
        this.queryClient.setQueriesData({ queryKey: ['content'] }, oldData => {
          if (!oldData) return oldData;

          // Handle infinite query data structure
          if (oldData.pages) {
            return {
              ...oldData,
              pages: oldData.pages.map(page => ({
                ...page,
                content: page.content.map(item => {
                  if (item.id === document.id) {
                    const updatedItem = { ...item };
                    urlProps.forEach(prop => {
                      if (document[prop]) {
                        updatedItem[prop] = this.ensureHttps(document[prop]);
                      }
                    });
                    return updatedItem;
                  }
                  return item;
                })
              }))
            };
          }

          // Handle regular array data structure
          if (Array.isArray(oldData.content)) {
            return {
              ...oldData,
              content: oldData.content.map(item => {
                if (item.id === document.id) {
                  const updatedItem = { ...item };
                  urlProps.forEach(prop => {
                    if (document[prop]) {
                      updatedItem[prop] = this.ensureHttps(document[prop]);
                    }
                  });
                  return updatedItem;
                }
                return item;
              })
            };
          }

          return oldData;
        });
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import './gallery/ImagesGrid.scss';
</style>

<style lang="scss">
// Global styles (not scoped)

// Fix for Quasar menu height issues that happen after navigation
.gallery-menu {
  max-width: 250px;
  max-height: none !important;
  height: auto !important;
  overflow-y: visible !important;

  .q-menu__content {
    max-height: none !important;
    height: auto !important;
    overflow-y: visible !important;
  }

  .q-virtual-scroll__content {
    max-height: none !important;
    height: auto !important;
    overflow-y: visible !important;
  }
}

/* Ensure gallery menus work correctly regardless of parent constraints */
.q-menu {
  &:has(.gallery-menu-list),
  &:has(.gallery-menu-item) {
    max-height: none !important;
    height: auto !important;
    overflow-y: visible !important;

    * {
      max-height: none !important;
      overflow-y: visible !important;
    }
  }
}
// Global styles for specific elements
.custom-label {
  @include inter-font($size: 14px, $bolded: false, $color: var(--main-text-color));
  white-space: nowrap;
}

.warning-label {
  @include inter-font($size: 14px, $bolded: false, $color: var(--negative));
  white-space: nowrap;
}

.gallery-menu {
  overflow-y: visible;

  .q-item {
    padding: 8px 16px;
    min-height: 40px;

    &:hover {
      background-color: rgba(0, 0, 0, 0.05) !important;
    }
  }

  .q-item-section + .q-item-section {
    margin-left: 8px;
  }

  .gallery-menu-label {
    white-space: nowrap;
  }
}

/* Chip styling in the image overlay */
.image-overlay {
  .content-id-chip,
  .content-dimensions-chip,
  .content-type-chip {
    .q-icon {
      display: flex;
      justify-content: center;
      align-items: center;
      color: var(--main-text-color);
      font-size: 16px;
      width: 25px;
      height: 25px;
    }

    .q-chip__content,
    span {
      @include inter-font($size: 10px, $bolded: true, $color: var(--main-text-color));
      line-height: 1;
      font-weight: 600 !important;
      font-family: 'Inter', sans-serif !important;
    }
  }
}

/* Dynamic island chip styling */
.image-chip,
.video-chip,
.document-chip {
  .q-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 16px;
    width: 25px;
    height: 25px;
  }
}

.image-chip {
  background-color: var(--image-chip-bg) !important;
  color: var(--image-chip-text) !important;
  border: 1px solid var(--image-chip-border) !important;

  .q-icon {
    color: var(--image-chip-text) !important;
  }
}

.video-chip {
  background-color: var(--video-chip-bg) !important;
  color: var(--video-chip-text) !important;
  border: 1px solid var(--video-chip-border) !important;

  .q-icon {
    color: var(--video-chip-text) !important;
  }
}

.document-chip {
  background-color: var(--document-chip-bg) !important;
  color: var(--document-chip-text) !important;
  border: 1px solid var(--document-chip-border) !important;

  .q-icon {
    color: var(--document-chip-text) !important;
  }
}
</style>
